/**
 * Bottom-centre HUD (DESIGN 6.5, 6.7): the big 5-slot queue bar with element
 * glyphs, the eight key tiles that light on press and show what each press
 * would do to the current queue, the four spell preview cards (one per cast
 * mode, the held mode highlighted), the local HP bar with ghost drain and
 * status icons, and the queue feedback animations: added slot pops,
 * combined slot flashes, cancelled slot flashes red and slides out, full
 * queue shakes, self-shock bursts every slot and shows the "Wet!" tooltip.
 *
 * Preview numbers are summed from the SpellSpec packets here so the HUD
 * never calls the shared rules stubs.
 */

import { ELEMENT_LIST, MAGICKS, MAX_QUEUE, isBase, type BaseElement, type CastMode, type Element } from '@shared/index.js';
import type { LocalView, QueueFeedbackOutcome } from '../state/viewModel.js';
import { elementColor, ELEMENT_SWATCH, hpColor, mixHex, rgba, STATUS_COLORS, UI } from '../palette.js';
import { clamp01, easeOutBack, font, noise1D, roundRectPath, type Ctx } from './gfx.js';
import { drawElementGlyph, drawElementIcon, drawElementMark, drawFormIcon, drawMagickIcon, drawModeIcon, iconsReady } from './glyphs.js';
import { drawStatusIcons } from './statusFx.js';
import { dominantOf } from './entities.js';
import { specDanger, specNumbers, tileHint } from './preview.js';
import {
  CARD_H, CARD_W, GAP, HP_H, hpBarLayout, KEY, KEY_GAP, KEY_ROW_W, KEY_SIDE_GAP, magickPillLayout, PILL_MAX_W, QUEUE_W, queueTop, SLOT,
} from './hudLayout.js';

const KEY_ROWS: readonly (readonly [string, BaseElement])[][] = [
  [['Q', 'water'], ['W', 'life'], ['E', 'shield'], ['R', 'cold']],
  [['A', 'lightning'], ['S', 'arcane'], ['D', 'earth'], ['F', 'fire']],
];

/** Key cap labels per element; settings.ts replaces these when the player rebinds. */
const keyLabels: Record<BaseElement, string> = { water: 'Q', life: 'W', shield: 'E', cold: 'R', lightning: 'A', arcane: 'S', earth: 'D', fire: 'F' };
/** Touch devices have on-screen element buttons, so the HUD key row is hidden there. */
let touchMode = false;
export function setHudTouchMode(on: boolean): void {
  touchMode = on;
}

export function setElementKeyLabels(labels: Partial<Record<BaseElement, string>>): void {
  Object.assign(keyLabels, labels);
}

/** Key caps of the Magick binding (Space, then its alias G); settings.ts replaces them when the player rebinds. */
let magickKeyLabels: readonly string[] = ['Space', 'G'];
export function setMagickKeyLabels(labels: readonly string[]): void {
  magickKeyLabels = labels.filter((l) => l.length > 0);
}

/** What casts a Magick, as the HUD names it: the bound keys, or the Magick button with touch controls. */
export function magickKeys(): readonly string[] {
  if (touchMode) return ['Magick'];
  return magickKeyLabels.length > 0 ? magickKeyLabels : ['Space'];
}

const MODE_ORDER: readonly { mode: CastMode; label: string; key: string }[] = [
  { mode: 'force', label: 'Force', key: 'LMB' },
  { mode: 'area', label: 'Area', key: 'Shift+LMB' },
  { mode: 'self', label: 'Self', key: 'MMB' },
  { mode: 'weapon', label: 'Weapon', key: 'V' },
];

/** Seconds the Magick pill shakes and flashes after a press the cooldown refused. */
const MAGICK_DENIED_S = 0.45;

interface Slot { el: Element | null; scale: number; flash: number; flashColor: string; slide: number; ghost: Element | null }

export class QueueHud {
  private readonly slots: Slot[] = [];
  private readonly prev: (Element | null)[] = [null, null, null, null, null];
  private prevLen = 0;
  private lastFeedbackAt = -1;
  private readonly keyLit: Record<BaseElement, number> = { water: 0, life: 0, shield: 0, cold: 0, lightning: 0, arcane: 0, earth: 0, fire: 0 };
  private readonly keyColor: Record<BaseElement, string> = { water: UI.white, life: UI.white, shield: UI.white, cold: UI.white, lightning: UI.white, arcane: UI.white, earth: UI.white, fire: UI.white };
  private shakeT = 0;
  private castT = 0;
  private wetT = 0;
  private hpGhost = -1;
  private readyPulse = 0;
  /** A Magick press the cooldown refused: the pill shakes and flashes for this long. */
  private magickDeniedT = 0;

  constructor() {
    for (let i = 0; i < MAX_QUEUE; i++) this.slots.push({ el: null, scale: 1, flash: 0, flashColor: UI.white, slide: 0, ghost: null });
  }

  /** Nudge the whole bar (queue full). */
  shake(): void { this.shakeT = 0.3; }
  /** "Wet!" tooltip over the Lightning key. */
  wet(): void { this.wetT = 1.6; }
  /** Local cast or magick: slots get sucked toward the cursor. */
  onLocalCast(): void {
    this.castT = 0.15;
    for (const s of this.slots) s.ghost = s.el;
  }

  update(me: LocalView | null, dt: number): void {
    for (const s of this.slots) {
      s.scale += (1 - s.scale) * Math.min(1, dt * 14);
      s.flash = Math.max(0, s.flash - dt * 5);
      s.slide = Math.max(0, s.slide - dt * 4);
    }
    for (const k of ELEMENT_LIST) this.keyLit[k] = Math.max(0, this.keyLit[k] - dt * 4);
    this.shakeT = Math.max(0, this.shakeT - dt);
    this.castT = Math.max(0, this.castT - dt);
    this.wetT = Math.max(0, this.wetT - dt);
    this.magickDeniedT = Math.max(0, this.magickDeniedT - dt);
    this.readyPulse += dt;
    if (!me) return;
    const q = me.queue;
    const fb = me.queueFeedback;
    if (fb && fb.at !== this.lastFeedbackAt) {
      this.lastFeedbackAt = fb.at;
      this.applyFeedback(fb.outcome, fb.el, q);
    } else if (!fb) {
      this.diffFallback(q);
    }
    for (let i = 0; i < MAX_QUEUE; i++) {
      const el = q[i] ?? null;
      this.slots[i]!.el = el;
      this.prev[i] = el;
    }
    this.prevLen = q.length;
    if (this.hpGhost < 0) this.hpGhost = me.hp;
    if (me.hp < this.hpGhost) this.hpGhost = Math.max(me.hp, this.hpGhost - dt * me.maxHp * 1.5);
    else this.hpGhost = me.hp;
  }

  private changedSlot(q: readonly Element[]): number {
    for (let i = 0; i < MAX_QUEUE; i++) if ((q[i] ?? null) !== this.prev[i]) return i;
    return -1;
  }

  private applyFeedback(outcome: QueueFeedbackOutcome, el: Element | null, q: readonly Element[]): void {
    switch (outcome) {
      case 'added': {
        const i = q.length - 1;
        if (i >= 0) { this.slots[i]!.scale = 1.35; this.slots[i]!.flash = 0.6; this.slots[i]!.flashColor = UI.white; }
        if (el && isBase(el)) this.lit(el, UI.white);
        return;
      }
      case 'combined': case 'degraded': {
        const i = this.changedSlot(q);
        if (i >= 0) { this.slots[i]!.flash = 1; this.slots[i]!.flashColor = outcome === 'combined' ? UI.white : ELEMENT_SWATCH.water.core; this.slots[i]!.scale = 1.25; }
        // Steam comes from Fire, Ice from Cold; a degrade to Water came from the key opposite to what was there.
        const pressed = pressedKeyFor(outcome, el, this.prev.filter((x): x is Element => x !== null));
        this.lit(pressed, outcome === 'combined' ? UI.white : ELEMENT_SWATCH.water.core);
        return;
      }
      case 'cancelled': {
        const i = this.changedSlot(q);
        const idx = i >= 0 ? i : Math.max(0, this.prevLen - 1);
        const s = this.slots[idx]!;
        s.flash = 1; s.flashColor = UI.ff; s.slide = 1; s.ghost = this.prev[idx] ?? null;
        if (el && isBase(el)) this.lit(el, UI.ff);
        return;
      }
      case 'full':
        this.shakeT = 0.3;
        if (el && isBase(el)) this.lit(el, UI.muted);
        return;
      case 'self_shock':
        for (const s of this.slots) { s.flash = 1; s.flashColor = ELEMENT_SWATCH.lightning.core; s.slide = 1; s.ghost = s.el; }
        this.lit('lightning', UI.ff);
        this.wetT = 1.6;
        this.shakeT = 0.4;
        return;
      case 'fizzle':
        for (let i = q.length; i < this.prevLen; i++) { const s = this.slots[i]!; s.flash = 0.6; s.flashColor = UI.muted; s.slide = 0.6; s.ghost = this.prev[i] ?? null; }
        return;
      case 'magick_wait':
        this.magickDeniedT = MAGICK_DENIED_S;
        return;
    }
  }

  /** Without a queueFeedback field, infer the animation from the queue diff. */
  private diffFallback(q: readonly Element[]): void {
    if (q.length > this.prevLen) {
      const i = q.length - 1;
      this.slots[i]!.scale = 1.35;
      const el = q[i]!;
      if (isBase(el)) this.lit(el, UI.white);
    } else if (q.length === this.prevLen) {
      const i = this.changedSlot(q);
      if (i >= 0) { this.slots[i]!.flash = 1; this.slots[i]!.flashColor = UI.white; this.slots[i]!.scale = 1.25; }
    } else if (q.length === this.prevLen - 1 && this.castT === 0) {
      const i = this.changedSlot(q);
      const idx = i >= 0 ? i : this.prevLen - 1;
      const s = this.slots[idx]!;
      s.flash = 1; s.flashColor = UI.ff; s.slide = 1; s.ghost = this.prev[idx] ?? null;
    }
  }

  private lit(el: BaseElement, color: string): void {
    this.keyLit[el] = 1;
    this.keyColor[el] = color;
  }

  // ---------------------------------------------------------------- drawing

  draw(ctx: Ctx, w: number, h: number, me: LocalView, held: CastMode, s: number, time: number): void {
    const barW = QUEUE_W * s;
    const shake = this.shakeT > 0 ? noise1D(time * 60) * 6 * s * (this.shakeT / 0.3) : 0;
    const cx = w / 2 + shake;
    // Kept deliberately small: the queue and one preview of what the next cast does.
    // The full per-mode breakdown and the key chart live in the spellbook (Tab).
    const barY = queueTop(h, s);
    if (me.queue.length > 0) this.drawCards(ctx, me, cx, barY - 10 * s, held, s, time);
    this.drawSlots(ctx, me, cx - barW / 2, barY, s, time);
    this.drawMagickPill(ctx, me, w, h, s);
    // Element keys flank the queue on the same line: always visible, no extra rows of UI.
    if (!touchMode) this.drawKeys(ctx, me, cx, barY + (SLOT - KEY) * s / 2, s, barW);
    this.drawImbue(ctx, me, cx, barY - 8 * s, s, time);
    // A full-width HP bar would run under the Q and W tiles on 4:3 windows, or under the
    // first queue slot on narrow landscape phones, so it shrinks or moves (hpBarLayout).
    const hp = hpBarLayout(w, h, s, !touchMode);
    this.drawHp(ctx, me, hp.x, hp.y, hp.width, s);
  }

  private drawSlots(ctx: Ctx, me: LocalView, x0: number, y: number, s: number, time: number): void {
    const size = SLOT * s;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let i = 0; i < MAX_QUEUE; i++) {
      const slot = this.slots[i]!;
      const cx = x0 + i * (SLOT + GAP) * s + size / 2;
      const cy = y + size / 2;
      // base
      ctx.fillStyle = UI.panel;
      roundRectPath(ctx, cx - size / 2, cy - size / 2, size, size, 10 * s);
      ctx.fill();
      ctx.strokeStyle = UI.panelEdge;
      ctx.lineWidth = 1;
      ctx.stroke();
      // sliding-out ghost of a cancelled / fizzled / shocked element
      if (slot.slide > 0 && slot.ghost) {
        const f = 1 - slot.slide;
        ctx.globalAlpha = slot.slide * 0.9;
        const gx = cx + f * 50 * s * (slot.flashColor === UI.ff ? 1 : 0.4) + noise1D(time * 50 + i) * 4 * s * slot.slide;
        const gy = cy + f * 30 * s;
        // with the pixel icons a tile is a dark tint of its element, so the full-colour icon reads
        const icons = iconsReady();
        const ghostFill = mixHex(elementColor(slot.ghost), slot.flashColor, 0.6);
        ctx.fillStyle = icons ? mixHex(UI.panelSolid, ghostFill, 0.4) : ghostFill;
        roundRectPath(ctx, gx - size / 2, gy - size / 2, size, size, 10 * s);
        ctx.fill();
        ctx.fillStyle = ELEMENT_SWATCH[slot.ghost].dark;
        if (!drawElementIcon(ctx, slot.ghost, gx, gy, size * 0.62)) drawElementGlyph(ctx, slot.ghost, gx, gy, size * 0.5);
        ctx.globalAlpha = 1;
      }
      const el = slot.el;
      if (el) {
        const sc = this.castT > 0 ? 1 - (0.15 - this.castT) / 0.15 : easeOutBack(clamp01(1 - (slot.scale - 1) / 0.35)) * 0.2 + 0.8 + (slot.scale - 1) * 0.5;
        const sz = size * sc;
        const dy = this.castT > 0 ? -(0.15 - this.castT) * 160 * s : 0;
        const icons = iconsReady();
        const fill = mixHex(elementColor(el), slot.flashColor, slot.flash * 0.8);
        ctx.fillStyle = icons ? mixHex(UI.panelSolid, fill, 0.32 + slot.flash * 0.4) : fill;
        roundRectPath(ctx, cx - sz / 2, cy + dy - sz / 2, sz, sz, 10 * s);
        ctx.fill();
        ctx.strokeStyle = rgba(elementColor(el), icons ? 1 : 0.8);
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.fillStyle = ELEMENT_SWATCH[el].dark;
        const fusedKeys = FUSED_KEYS[el];
        if (!drawElementIcon(ctx, el, cx, cy + dy - (fusedKeys ? 3 * s : 0), sz * 0.6)) drawElementGlyph(ctx, el, cx, cy + dy, sz * 0.52);
        if (fusedKeys) {
          ctx.font = font(9 * s, 700);
          ctx.fillStyle = icons ? UI.text : ELEMENT_SWATCH[el].dark;
          ctx.fillText(fusedKeys, cx, cy + dy + sz * 0.38);
        }
      } else {
        ctx.fillStyle = 'rgba(255,255,255,0.08)';
        roundRectPath(ctx, cx - size * 0.3, cy - size * 0.3, size * 0.6, size * 0.6, 6 * s);
        ctx.fill();
      }
    }
  }

  /**
   * The Magick the queue spells, over the cast preview (hudLayout.magickPillLayout): its name and
   * the key that casts it, or, while the PvP cooldown runs, a greyed icon inside a countdown ring
   * and "ready in 12 s". A press the cooldown refused shakes it and flashes its edge red.
   */
  private drawMagickPill(ctx: Ctx, me: LocalView, w: number, h: number, s: number): void {
    const id = me.magickHint.find((m) => MAGICKS[m].seq.length === me.queue.length);
    if (!id) return;
    const cd = me.magickCd;
    const name = MAGICKS[id].name;
    const keys = magickKeys();
    const icon = 24 * s, pad = 12 * s, gap = 8 * s;
    const nameFont = font(17 * s, 800), keyFont = font(13 * s, 800), noteFont = font(14 * s, 700);
    // right-hand part: [Space] or [G] while it can go, "ready in 12 s" while it can't
    ctx.font = keyFont;
    const capW = keys.map((k) => ctx.measureText(k).width + 12 * s);
    const orW = ctx.measureText('or').width;
    const note = cd ? `ready in ${Math.ceil(cd.left)} s` : '';
    ctx.font = noteFont;
    const tailW = cd ? ctx.measureText(note).width : capW.reduce((a, b) => a + b, 0) + (keys.length - 1) * (orW + 10 * s);
    const fixedW = pad + icon + gap + 12 * s + tailW + pad + 4 * s;
    // the pill keeps to its column (hudLayout PILL_MAX_W), so a long name gives way, not the key
    ctx.font = nameFont;
    const shown = fit(ctx, name, PILL_MAX_W * s - fixedW);
    const nameW = ctx.measureText(shown).width;
    const pillW = fixedW + nameW;
    const box = magickPillLayout(w, h, s, pillW);
    const denied = this.magickDeniedT / MAGICK_DENIED_S;
    const x = box.x + (denied > 0 ? Math.sin(denied * 40) * 7 * s * denied : 0);
    const cy = box.y + box.h / 2;
    const pulse = 0.8 + 0.2 * Math.sin(this.readyPulse * 6);

    ctx.fillStyle = 'rgba(16,18,28,0.94)';
    roundRectPath(ctx, x, box.y, pillW, box.h, box.h / 2);
    ctx.fill();
    ctx.strokeStyle = denied > 0 ? mixHex(UI.muted, UI.ff, denied) : cd ? rgba(UI.muted, 0.7) : rgba(UI.gold, pulse);
    ctx.lineWidth = 2;
    ctx.stroke();

    // icon, greyed inside a ring that empties as the cooldown runs out
    const ix = x + pad + icon / 2;
    if (cd) ctx.globalAlpha = 0.4;
    if (!drawMagickIcon(ctx, id, ix, cy, 20 * s)) {
      ctx.fillStyle = cd ? UI.muted : UI.gold;
      ctx.beginPath();
      ctx.arc(ix, cy, 6 * s, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    if (cd) {
      const r = icon / 2 + 2 * s;
      ctx.strokeStyle = 'rgba(255,255,255,0.12)';
      ctx.lineWidth = 2.5 * s;
      ctx.beginPath();
      ctx.arc(ix, cy, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = UI.text;
      ctx.beginPath();
      ctx.arc(ix, cy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * clamp01(cd.left / cd.total));
      ctx.stroke();
    }

    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    let tx = ix + icon / 2 + gap;
    ctx.font = nameFont;
    ctx.fillStyle = cd ? UI.muted : UI.gold;
    ctx.fillText(shown, tx, cy + 1);
    tx += nameW + 12 * s;
    if (cd) {
      ctx.font = noteFont;
      ctx.fillStyle = denied > 0 ? mixHex(UI.text, UI.ff, denied) : UI.text;
      ctx.fillText(note, tx, cy + 1);
      return;
    }
    ctx.font = keyFont;
    keys.forEach((k, i) => {
      if (i > 0) {
        ctx.fillStyle = UI.muted;
        ctx.fillText('or', tx + 5 * s, cy + 1);
        tx += orW + 10 * s;
      }
      const kw = capW[i]!;
      ctx.fillStyle = rgba(UI.gold, 0.16);
      roundRectPath(ctx, tx, cy - 11 * s, kw, 22 * s, 5 * s);
      ctx.fill();
      ctx.strokeStyle = rgba(UI.gold, 0.6);
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = UI.text;
      ctx.fillText(k, tx + 6 * s, cy + 1);
      tx += kw;
    });
  }

  /** Imbued staff: the elements riding the blade and the swings left, just above the queue. */
  private drawImbue(ctx: Ctx, me: LocalView, cx: number, y: number, s: number, time: number): void {
    const els = me.imb;
    if (!els || els.length === 0 || !me.imc) return;
    if (me.queue.length > 0) return; // the cast preview owns this spot while you are queueing
    ctx.font = font(11 * s, 700);
    ctx.textBaseline = 'middle';
    const label = `Staff x${me.imc}`;
    const tw = ctx.measureText(label).width;
    const w = tw + els.length * 16 * s + 26 * s;
    const x = cx - w / 2;
    ctx.fillStyle = UI.panel;
    roundRectPath(ctx, x, y - 22 * s, w, 22 * s, 8 * s);
    ctx.fill();
    ctx.strokeStyle = rgba(elementColor(els[0]!), 0.6 + 0.3 * Math.sin(time * 5));
    ctx.lineWidth = 1.5;
    ctx.stroke();
    let gx = x + 12 * s;
    for (const el of els) {
      ctx.fillStyle = elementColor(el);
      drawElementMark(ctx, el, gx, y - 11 * s, 13 * s);
      gx += 16 * s;
    }
    ctx.fillStyle = UI.text;
    ctx.textAlign = 'left';
    ctx.fillText(label, gx + 2 * s, y - 11 * s);
  }

  private drawKeys(ctx: Ctx, me: LocalView, cx: number, y: number, s: number, barW: number): void {
    const k = KEY * s, g = KEY_GAP * s;
    const rowW = KEY_ROW_W * s;
    const x0 = cx - barW / 2 - KEY_SIDE_GAP * s - rowW;
    const x1 = cx + barW / 2 + KEY_SIDE_GAP * s;
    ctx.textBaseline = 'middle';
    for (let r = 0; r < 2; r++) {
      const row = KEY_ROWS[r]!;
      for (let i = 0; i < 4; i++) {
        const el = row[i]![1];
        const label = keyLabels[el];
        const x = (r === 0 ? x0 : x1) + i * (k + g);
        const hint = tileHint(me.queue, el, me.wet);
        const lit = this.keyLit[el];
        const tileAlpha = hint.kind === 'full' ? 0.4 : 1;
        ctx.globalAlpha = tileAlpha;
        const grow = lit * 3 * s;
        ctx.fillStyle = lit > 0 ? mixHex(UI.panelSolid, this.keyColor[el], lit * 0.5) : UI.panel;
        roundRectPath(ctx, x - grow, y - grow, k + grow * 2, k + grow * 2, 7 * s);
        ctx.fill();
        ctx.strokeStyle = hint.kind === 'shock' && Math.floor(this.readyPulse * 2) % 2 === 0 ? UI.ff : lit > 0 ? rgba(this.keyColor[el], lit) : UI.panelEdge;
        ctx.lineWidth = lit > 0 ? 2 : 1;
        ctx.stroke();
        ctx.font = font(9 * s, 700);
        ctx.textAlign = 'left';
        ctx.fillStyle = UI.muted;
        ctx.fillText(label, x + 4 * s, y + 7 * s);
        const gx = x + k / 2, gy = y + k / 2 + 3 * s;
        if (hint.kind === 'combine') {
          ctx.fillStyle = elementColor(el);
          drawElementMark(ctx, el, gx - 7 * s, gy, 13 * s, 0.6);
          ctx.fillStyle = elementColor(hint.into);
          drawElementMark(ctx, hint.into, gx + 7 * s, gy, 15 * s);
        } else if (hint.kind === 'shock') {
          ctx.fillStyle = UI.ff;
          drawElementMark(ctx, el, gx, gy - 3 * s, 16 * s);
          ctx.fillStyle = UI.ff;
          ctx.font = font(8 * s, 800);
          ctx.textAlign = 'center';
          ctx.fillText('WET', gx, y + k - 6 * s);
        } else {
          ctx.fillStyle = elementColor(el);
          drawElementMark(ctx, el, gx, gy, 18 * s, hint.kind === 'cancel' ? 0.45 : 0.9);
          if (hint.kind === 'cancel') {
            ctx.strokeStyle = UI.ff;
            ctx.lineWidth = 2.5;
            ctx.beginPath();
            ctx.moveTo(x + 8 * s, y + k - 8 * s);
            ctx.lineTo(x + k - 8 * s, y + 8 * s);
            ctx.stroke();
          } else if (hint.kind === 'degrade') {
            ctx.fillStyle = ELEMENT_SWATCH.water.core;
            drawElementMark(ctx, 'water', x + k - 9 * s, y + k - 9 * s, 9 * s);
          }
        }
        ctx.globalAlpha = 1;
      }
    }
    if (this.wetT > 0) {
      const ax = x1 + k / 2;
      const ty = y - 16 * s;
      ctx.globalAlpha = Math.min(1, this.wetT * 2);
      ctx.font = font(11 * s, 800);
      const txt = 'Wet! Lightning shocks you';
      const tw = ctx.measureText(txt).width + 16 * s;
      ctx.fillStyle = rgba(ELEMENT_SWATCH.water.core, 0.9);
      roundRectPath(ctx, ax - tw / 2, ty - 11 * s, tw, 22 * s, 6 * s);
      ctx.fill();
      ctx.fillStyle = UI.white;
      ctx.fillText(txt, ax, ty);
      ctx.globalAlpha = 1;
    }
  }

  private drawCards(ctx: Ctx, me: LocalView, cx: number, bottom: number, held: CastMode, s: number, time: number): void {
    const cw = CARD_W * 1.3 * s, ch = CARD_H * s, gap = 0;
    const x0 = cx - cw / 2;
    const y = bottom - ch;
    ctx.textBaseline = 'middle';
    const shown = MODE_ORDER.filter((m) => m.mode === held);
    for (let i = 0; i < shown.length; i++) {
      const { mode, label, key } = shown[i]!;
      const spec = me.preview[mode];
      const name = me.previewName[mode];
      const x = x0 + i * (cw + gap);
      const active = mode === held;
      ctx.fillStyle = active ? 'rgba(40,44,62,0.92)' : UI.panel;
      roundRectPath(ctx, x, y, cw, ch, 8 * s);
      ctx.fill();
      ctx.strokeStyle = active ? rgba(UI.gold, 0.8 + 0.2 * Math.sin(time * 6)) : UI.panelEdge;
      ctx.lineWidth = active ? 2 : 1;
      ctx.stroke();
      const dom = spec.kind === 'none' ? null : dominantOf(me.queue);
      const col = dom ? elementColor(dom) : UI.muted;
      ctx.fillStyle = col;
      ctx.strokeStyle = col;
      drawFormIcon(ctx, spec.kind, x + 18 * s, y + ch / 2, 22 * s);
      ctx.textAlign = 'left';
      ctx.font = font(9 * s, 700);
      ctx.fillStyle = active ? UI.gold : UI.muted;
      // the cast mode's pixel icon leads its label once the icons have landed
      const lx = drawModeIcon(ctx, mode, x + 42 * s, y + 11 * s, 11 * s) ? x + 50 * s : x + 36 * s;
      ctx.fillText(`${label.toUpperCase()}  ${key}`, lx, y + 11 * s);
      ctx.font = font(12 * s, 700);
      ctx.fillStyle = UI.text;
      ctx.fillText(fit(ctx, name, cw - 42 * s), x + 36 * s, y + 27 * s);
      ctx.font = font(10 * s, 600);
      ctx.fillStyle = UI.muted;
      ctx.fillText(fit(ctx, specNumbers(spec), cw - 42 * s), x + 36 * s, y + 42 * s);
      const danger = specDanger(spec, me.hp);
      if (danger) {
        ctx.font = font(9 * s, 800);
        ctx.fillStyle = UI.ff;
        ctx.fillText(danger, x + 36 * s, y + 55 * s);
      }
    }
  }

  private drawHp(ctx: Ctx, me: LocalView, x: number, y: number, bw: number, s: number): void {
    const bh = HP_H * s;
    const frac = me.maxHp > 0 ? clamp01(me.hp / me.maxHp) : 0;
    const ghost = me.maxHp > 0 ? clamp01(this.hpGhost / me.maxHp) : 0;
    ctx.fillStyle = UI.panel;
    roundRectPath(ctx, x - 3, y - 3, bw + 6, bh + 6, 6 * s);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.08)';
    ctx.fillRect(x, y, bw, bh);
    ctx.fillStyle = rgba(UI.white, 0.5);
    ctx.fillRect(x, y, bw * ghost, bh);
    const col = hpColor(frac);
    ctx.fillStyle = frac < 0.25 ? mixHex(col, '#FFFFFF', 0.3 + 0.3 * Math.sin(this.readyPulse * 10)) : col;
    ctx.fillRect(x, y, bw * frac, bh);
    ctx.font = font(12 * s, 800);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = UI.white;
    ctx.fillText(`${Math.max(0, Math.round(me.hp))} / ${me.maxHp}`, x + bw / 2, y + bh / 2);
    drawStatusIcons(ctx, me.statuses, x + 60 * s + Math.max(0, me.statuses.length - 1) * 14 * s, y + bh + 16 * s, 12 * s);
    // status names with remaining seconds, for the exact readout
    ctx.font = font(10 * s, 600);
    ctx.textAlign = 'left';
    let tx = x + 130 * s;
    for (const st of me.statuses) {
      const label = `${st.id}${st.id === 'burning' && st.s > 1 ? ` x${st.s}` : ''} ${Math.ceil(st.t)}s`;
      ctx.fillStyle = STATUS_COLORS[st.id];
      ctx.fillText(label, tx, y + bh + 16 * s);
      tx += ctx.measureText(label).width + 10 * s;
    }
  }
}

function fit(ctx: Ctx, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxW) t = t.slice(0, -1);
  return `${t}…`;
}

/** Which key most likely produced a fuse or breakdown, to light it on the HUD. */
function pressedKeyFor(outcome: 'combined' | 'degraded', el: Element | null, prev: readonly Element[]): BaseElement {
  if (outcome === 'combined') {
    switch (el) {
      case 'steam': return 'fire';
      case 'ice': return 'cold';
      case 'mud': return 'earth';
      case 'lava': return 'earth';
      case 'spring': return 'life';
      default: return 'water';
    }
  }
  if (prev.includes('ice') || prev.includes('mud')) return 'fire';
  return 'cold';
}

/** Key pair shown under a fused element in the queue slot. */
const FUSED_KEYS: Partial<Record<Element, string>> = { steam: 'Q+F', ice: 'Q+R', mud: 'Q+D', lava: 'F+D', spring: 'Q+W' };
